
		import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "dreamland/jsx-runtime";
export const title = "Notes on reverse-engineering the Apple Music API";
export const tags = ["Writeup", "Webdev"];
export const image = "/blog-images/apple-park-photo-1-building-trees.jpg";
export const description = "Some notes on the endpoints of Apple Music’s private REST API.";
function _createMdxContent(props) {
  const _components = {
    a: "a",
    blockquote: "blockquote",
    code: "code",
    h2: "h2",
    h3: "h3",
    img: "img",
    p: "p",
    pre: "pre",
    span: "span",
    strong: "strong",
    ...props.components
  };
  return _jsxs(_Fragment, {
    children: [_jsx(_components.p, {
      children: _jsx(_components.img, {
        src: "/blog-images/apple-park-photo-1-building-trees.jpg",
        alt: "Apple Park"
      })
    }), "\n", _jsx("cap", {
      children: _jsxs("subt", {
        children: [_jsx("span", {
          class: "material-symbols",
          children: "info"
        }), "Image credit: Apple."]
      })
    }), "\n", _jsxs(_components.p, {
      children: ["I've recently been working on ", _jsx(_components.a, {
        href: "https://bomberfish.ca/Transcribe",
        children: "Transcribe"
      }), ", a tool which transfers playlists between music streaming services directly inside your browser (", _jsx(_components.a, {
        href: "https://youtu.be/MshSHxytkdQ",
        children: "Demo video"
      }), "). To add support for Apple Music, I had to reverse-engineer its API. Now, you may be wondering:"]
    }), "\n", _jsx(_components.h2, {
      children: "Why not just use MusicKit?"
    }), "\n", _jsxs(_components.p, {
      children: ["For anyone who doesn't know, MusicKit (JS) is Apple Music's ", _jsx(_components.strong, {
        children: "official API"
      }), ". The issue with MusicKit is that using it requires an active Apple Developer Program membership. This would require me to pay Apple almost $150 every year, which is money I do not have. So, I had to resort to a more legally dubious method."]
    }), "\n", _jsx(_components.p, {
      children: "Now without any further ado, let's get into the cool stuff!"
    }), "\n", _jsxs(_components.blockquote, {
      children: ["\n", _jsx(_components.p, {
        children: "Please note that this is far from complete and should not be considered complete documentation. It might be updated in the future. Also, please hire me Apple :3"
      }), "\n"]
    }), "\n", _jsx(_components.h2, {
      children: "How I did it"
    }), "\n", _jsxs(_components.p, {
      children: ["This is very simple. I simply opened ", _jsx(_components.a, {
        href: "https://beta.music.apple.com",
        children: "Apple Music's web client"
      }), " in my browser, opened the Network tab in Chrome's DevTools, and watched any fetch requests it made."]
    }), "\n", _jsxs(_components.p, {
      children: ["The base URL for all requests is ", _jsx(_components.code, {
        children: "https://amp-api.music.apple.com"
      }), "."]
    }), "\n", _jsx(_components.h2, {
      children: "Authentication"
    }), "\n", _jsxs(_components.p, {
      children: ["The API seems to need two pieces of information to accept requests: a user token (via the ", _jsx(_components.code, {
        children: "Authentication"
      }), " header) and some cookies."]
    }), "\n", _jsxs(_components.p, {
      children: ["As for the cookies, I haven't looked into what parts of the cookie the API actually requires to be there, but pasting everything sent in the ", _jsx(_components.code, {
        children: "Cookie"
      }), " header works fine."]
    }), "\n", _jsx(_components.h2, {
      children: "Common data types"
    }), "\n", _jsx(_components.p, {
      children: "Here are a selection of TypeScript types from Transcribe. I'll refer to them later in example responses."
    }), "\n", _jsx(_components.pre, {
      children: _jsxs(_components.code, {
        class: "language-ts",
        children: [_jsx(_components.span, {
          class: "pl-c",
          children: "/**"
        }), "\n", _jsx(_components.span, {
          class: "pl-c",
          children: " * Represents a generic reference to an Apple Music resource."
        }), "\n", _jsx(_components.span, {
          class: "pl-c",
          children: " */"
        }), "\n", _jsx(_components.span, {
          class: "pl-k",
          children: "interface"
        }), " ", _jsx(_components.span, {
          class: "pl-en",
          children: "AMResourceReference"
        }), " {\n  ", _jsx(_components.span, {
          class: "pl-v",
          children: "id"
        }), _jsx(_components.span, {
          class: "pl-k",
          children: ":"
        }), " ", _jsx(_components.span, {
          class: "pl-c1",
          children: "string"
        }), ";\n  ", _jsx(_components.span, {
          class: "pl-v",
          children: "type"
        }), _jsx(_components.span, {
          class: "pl-k",
          children: ":"
        }), " ", _jsx(_components.span, {
          class: "pl-en",
          children: "AMResourceType"
        }), ";\n  ", _jsx(_components.span, {
          class: "pl-v",
          children: "href"
        }), _jsx(_components.span, {
          class: "pl-k",
          children: ":"
        }), " ", _jsx(_components.span, {
          class: "pl-c1",
          children: "string"
        }), ";\n}\n\n", _jsx(_components.span, {
          class: "pl-c",
          children: "/**"
        }), "\n", _jsx(_components.span, {
          class: "pl-c",
          children: " * Represents a relationship link to other resources."
        }), "\n", _jsx(_components.span, {
          class: "pl-c",
          children: " */"
        }), "\n", _jsx(_components.span, {
          class: "pl-k",
          children: "interface"
        }), " ", _jsx(_components.span, {
          class: "pl-en",
          children: "AMRelationship"
        }), "<", _jsx(_components.span, {
          class: "pl-en",
          children: "T"
        }), " ", _jsx(_components.span, {
          class: "pl-k",
          children: "extends"
        }), " ", _jsx(_components.span, {
          class: "pl-en",
          children: "AMResourceReference"
        }), "> {\n  ", _jsx(_components.span, {
          class: "pl-v",
          children: "href"
        }), _jsx(_components.span, {
          class: "pl-k",
          children: ":"
        }), " ", _jsx(_components.span, {
          class: "pl-c1",
          children: "string"
        }), ";\n  ", _jsx(_components.span, {
          class: "pl-v",
          children: "data"
        }), _jsx(_components.span, {
          class: "pl-k",
          children: ":"
        }), " ", _jsx(_components.span, {
          class: "pl-en",
          children: "T"
        }), "[];\n  ", _jsx(_components.span, {
          class: "pl-v",
          children: "meta"
        }), _jsx(_components.span, {
          class: "pl-k",
          children: "?:"
        }), " { ", _jsx(_components.span, {
          class: "pl-c",
          children: "// Optional meta information, seen for tracks"
        }), "\n    ", _jsx(_components.span, {
          class: "pl-v",
          children: "total"
        }), _jsx(_components.span, {
          class: "pl-k",
          children: ":"
        }), " ", _jsx(_components.span, {
          class: "pl-c1",
          children: "number"
        }), ";\n  };\n}\n\n", _jsx(_components.span, {
          class: "pl-c",
          children: "/**"
        }), "\n", _jsx(_components.span, {
          class: "pl-c",
          children: " * Container for included resource objects, keyed by type, then ID."
        }), "\n", _jsx(_components.span, {
          class: "pl-c",
          children: " */"
        }), "\n", _jsx(_components.span, {
          class: "pl-k",
          children: "interface"
        }), " ", _jsx(_components.span, {
          class: "pl-en",
          children: "AMResourcesContainer"
        }), " {\n  ", _jsxs(_components.span, {
          class: "pl-s",
          children: [_jsx(_components.span, {
            class: "pl-pds",
            children: "'"
          }), "library-playlists", _jsx(_components.span, {
            class: "pl-pds",
            children: "'"
          })]
        }), _jsx(_components.span, {
          class: "pl-k",
          children: "?:"
        }), " { [", _jsx(_components.span, {
          class: "pl-v",
          children: "id"
        }), _jsx(_components.span, {
          class: "pl-k",
          children: ":"
        }), " ", _jsx(_components.span, {
          class: "pl-c1",
          children: "string"
        }), "]", _jsx(_components.span, {
          class: "pl-k",
          children: ":"
        }), " ", _jsx(_components.span, {
          class: "pl-en",
          children: "AMLibraryPlaylist"
        }), " };\n  ", _jsxs(_components.span, {
          class: "pl-s",
          children: [_jsx(_components.span, {
            class: "pl-pds",
            children: "'"
          }), "playlists", _jsx(_components.span, {
            class: "pl-pds",
            children: "'"
          })]
        }), _jsx(_components.span, {
          class: "pl-k",
          children: "?:"
        }), " { [", _jsx(_components.span, {
          class: "pl-v",
          children: "id"
        }), _jsx(_components.span, {
          class: "pl-k",
          children: ":"
        }), " ", _jsx(_components.span, {
          class: "pl-c1",
          children: "string"
        }), "]", _jsx(_components.span, {
          class: "pl-k",
          children: ":"
        }), " ", _jsx(_components.span, {
          class: "pl-en",
          children: "AMCatalogPlaylist"
        }), " };\n  ", _jsxs(_components.span, {
          class: "pl-s",
          children: [_jsx(_components.span, {
            class: "pl-pds",
            children: "'"
          }), "library-songs", _jsx(_components.span, {
            class: "pl-pds",
            children: "'"
          })]
        }), _jsx(_components.span, {
          class: "pl-k",
          children: "?:"
        }), " { [", _jsx(_components.span, {
          class: "pl-v",
          children: "id"
        }), _jsx(_components.span, {
          class: "pl-k",
          children: ":"
        }), " ", _jsx(_components.span, {
          class: "pl-c1",
          children: "string"
        }), "]", _jsx(_components.span, {
          class: "pl-k",
          children: ":"
        }), " ", _jsx(_components.span, {
          class: "pl-en",
          children: "AMLibrarySong"
        }), " };\n  ", _jsxs(_components.span, {
          class: "pl-s",
          children: [_jsx(_components.span, {
            class: "pl-pds",
            children: "'"
          }), "songs", _jsx(_components.span, {
            class: "pl-pds",
            children: "'"
          })]
        }), _jsx(_components.span, {
          class: "pl-k",
          children: "?:"
        }), " { [", _jsx(_components.span, {
          class: "pl-v",
          children: "id"
        }), _jsx(_components.span, {
          class: "pl-k",
          children: ":"
        }), " ", _jsx(_components.span, {
          class: "pl-c1",
          children: "string"
        }), "]", _jsx(_components.span, {
          class: "pl-k",
          children: ":"
        }), " ", _jsx(_components.span, {
          class: "pl-en",
          children: "AMCatalogSong"
        }), " };\n  ", _jsxs(_components.span, {
          class: "pl-s",
          children: [_jsx(_components.span, {
            class: "pl-pds",
            children: "'"
          }), "apple-curators", _jsx(_components.span, {
            class: "pl-pds",
            children: "'"
          })]
        }), _jsx(_components.span, {
          class: "pl-k",
          children: "?:"
        }), " { [", _jsx(_components.span, {
          class: "pl-v",
          children: "id"
        }), _jsx(_components.span, {
          class: "pl-k",
          children: ":"
        }), " ", _jsx(_components.span, {
          class: "pl-c1",
          children: "string"
        }), "]", _jsx(_components.span, {
          class: "pl-k",
          children: ":"
        }), " ", _jsx(_components.span, {
          class: "pl-en",
          children: "AMAppleCurator"
        }), " };\n  ", _jsxs(_components.span, {
          class: "pl-s",
          children: [_jsx(_components.span, {
            class: "pl-pds",
            children: "'"
          }), "artists", _jsx(_components.span, {
            class: "pl-pds",
            children: "'"
          })]
        }), _jsx(_components.span, {
          class: "pl-k",
          children: "?:"
        }), " { [", _jsx(_components.span, {
          class: "pl-v",
          children: "id"
        }), _jsx(_components.span, {
          class: "pl-k",
          children: ":"
        }), " ", _jsx(_components.span, {
          class: "pl-c1",
          children: "string"
        }), "]", _jsx(_components.span, {
          class: "pl-k",
          children: ":"
        }), " ", _jsx(_components.span, {
          class: "pl-en",
          children: "AMArtist"
        }), " };\n  ", _jsxs(_components.span, {
          class: "pl-s",
          children: [_jsx(_components.span, {
            class: "pl-pds",
            children: "'"
          }), "albums", _jsx(_components.span, {
            class: "pl-pds",
            children: "'"
          })]
        }), _jsx(_components.span, {
          class: "pl-k",
          children: "?:"
        }), " { [", _jsx(_components.span, {
          class: "pl-v",
          children: "id"
        }), _jsx(_components.span, {
          class: "pl-k",
          children: ":"
        }), " ", _jsx(_components.span, {
          class: "pl-c1",
          children: "string"
        }), "]", _jsx(_components.span, {
          class: "pl-k",
          children: ":"
        }), " ", _jsx(_components.span, {
          class: "pl-en",
          children: "AMAlbum"
        }), " };\n  ", _jsxs(_components.span, {
          class: "pl-s",
          children: [_jsx(_components.span, {
            class: "pl-pds",
            children: "'"
          }), "music-videos", _jsx(_components.span, {
            class: "pl-pds",
            children: "'"
          })]
        }), _jsx(_components.span, {
          class: "pl-k",
          children: "?:"
        }), " { [", _jsx(_components.span, {
          class: "pl-v",
          children: "id"
        }), _jsx(_components.span, {
          class: "pl-k",
          children: ":"
        }), " ", _jsx(_components.span, {
          class: "pl-c1",
          children: "string"
        }), "]", _jsx(_components.span, {
          class: "pl-k",
          children: ":"
        }), " ", _jsx(_components.span, {
          class: "pl-en",
          children: "AMMusicVideo"
        }), " };\n  ", _jsxs(_components.span, {
          class: "pl-s",
          children: [_jsx(_components.span, {
            class: "pl-pds",
            children: "'"
          }), "stations", _jsx(_components.span, {
            class: "pl-pds",
            children: "'"
          })]
        }), _jsx(_components.span, {
          class: "pl-k",
          children: "?:"
        }), " { [", _jsx(_components.span, {
          class: "pl-v",
          children: "id"
        }), _jsx(_components.span, {
          class: "pl-k",
          children: ":"
        }), " ", _jsx(_components.span, {
          class: "pl-c1",
          children: "string"
        }), "]", _jsx(_components.span, {
          class: "pl-k",
          children: ":"
        }), " ", _jsx(_components.span, {
          class: "pl-en",
          children: "AMStation"
        }), " };\n  ", _jsxs(_components.span, {
          class: "pl-s",
          children: [_jsx(_components.span, {
            class: "pl-pds",
            children: "'"
          }), "uploaded-videos", _jsx(_components.span, {
            class: "pl-pds",
            children: "'"
          })]
        }), _jsx(_components.span, {
          class: "pl-k",
          children: "?:"
        }), " { [", _jsx(_components.span, {
          class: "pl-v",
          children: "id"
        }), _jsx(_components.span, {
          class: "pl-k",
          children: ":"
        }), " ", _jsx(_components.span, {
          class: "pl-c1",
          children: "string"
        }), "]", _jsx(_components.span, {
          class: "pl-k",
          children: ":"
        }), " ", _jsx(_components.span, {
          class: "pl-en",
          children: "AMUploadedVideo"
        }), " };\\\n}\n"]
      })
    }), "\n", _jsx(_components.h2, {
      children: "Getting stuff from your Library"
    }), "\n", _jsx(_components.h3, {
      children: "Recently Added"
    }), "\n", _jsxs(_components.p, {
      children: [_jsx(_components.strong, {
        children: "Endpoint:"
      }), " ", _jsx(_components.code, {
        children: "GET /v1/me/library/recently-added"
      })]
    }), "\n", _jsx(_components.h3, {
      children: "All Playlists"
    }), "\n", _jsxs(_components.p, {
      children: [_jsx(_components.strong, {
        children: "Endpoint:"
      }), " ", _jsx(_components.code, {
        children: "GET /v1/me/library/playlists"
      })]
    }), "\n", _jsxs(_components.p, {
      children: ["You can limit the number of playlists returned with ", _jsxs(_components.strong, {
        children: ["the ", _jsx(_components.code, {
          children: "limit"
        }), " parameter."]
      }), "\n", _jsx(_components.strong, {
        children: "What the response would look like:"
      })]
    }), "\n", _jsx(_components.pre, {
      children: _jsxs(_components.code, {
        class: "language-ts",
        children: ["[\n  {\n\ttracks: ", _jsx(_components.span, {
          class: "pl-smi",
          children: "AMRelationship"
        }), _jsx(_components.span, {
          class: "pl-k",
          children: "<"
        }), _jsx(_components.span, {
          class: "pl-smi",
          children: "AMResourceReference"
        }), _jsx(_components.span, {
          class: "pl-k",
          children: ">"
        }), ",\n\tcatalog?: ", _jsx(_components.span, {
          class: "pl-smi",
          children: "AMRelationship"
        }), _jsx(_components.span, {
          class: "pl-k",
          children: "<"
        }), _jsx(_components.span, {
          class: "pl-smi",
          children: "AMResourceReference"
        }), _jsx(_components.span, {
          class: "pl-k",
          children: ">"
        }), ",\n  }\n]\n"]
      })
    }), "\n", _jsx(_components.h3, {
      children: "Songs in a specific playlist"
    }), "\n", _jsxs(_components.p, {
      children: [_jsx(_components.strong, {
        children: "Endpoint:"
      }), " ", _jsx(_components.code, {
        children: "GET /v1/me/library/playlists/:id"
      })]
    }), "\n", _jsx(_components.p, {
      children: _jsx(_components.strong, {
        children: "What the response would look like:"
      })
    }), "\n", _jsx(_components.pre, {
      children: _jsxs(_components.code, {
        class: "language-ts",
        children: ["[\n  {\n\tdata: ", _jsx(_components.span, {
          class: "pl-smi",
          children: "AMResourceReference"
        }), "[],\n\tresources?: ", _jsx(_components.span, {
          class: "pl-smi",
          children: "AMResourcesContainer"
        }), ",\n  }\n]\n"]
      })
    }), "\n", _jsx(_components.h2, {
      children: "Managing your Library"
    }), "\n", _jsx(_components.h3, {
      children: "Creating a Playlist"
    }), "\n", _jsxs(_components.p, {
      children: [_jsx(_components.strong, {
        children: "Endpoint:"
      }), " ", _jsx(_components.code, {
        children: "POST /v1/me/playlists"
      }), "\n", _jsx(_components.strong, {
        children: "What your body should look like:"
      })]
    }), "\n", _jsx(_components.pre, {
      children: _jsxs(_components.code, {
        class: "language-ts",
        children: ["{\n  ", _jsx(_components.span, {
          class: "pl-en",
          children: "attributes"
        }), ": {\n    ", _jsx(_components.span, {
          class: "pl-en",
          children: "name"
        }), ": ", _jsx(_components.span, {
          class: "pl-smi",
          children: "string"
        }), ";\n\t", _jsx(_components.span, {
          class: "pl-en",
          children: "description"
        }), ": ", _jsx(_components.span, {
          class: "pl-smi",
          children: "string"
        }), ";\n\t", _jsx(_components.span, {
          class: "pl-en",
          children: "isPublic"
        }), ": ", _jsx(_components.span, {
          class: "pl-smi",
          children: "boolean"
        }), ";\n  },\n  ", _jsx(_components.span, {
          class: "pl-en",
          children: "relationships"
        }), ": {\n  \t", _jsx(_components.span, {
          class: "pl-en",
          children: "tracks"
        }), ": {\n\t  ", _jsx(_components.span, {
          class: "pl-en",
          children: "data"
        }), ": [\n\t  \t", _jsx(_components.span, {
          class: "pl-smi",
          children: "id"
        }), ": ", _jsx(_components.span, {
          class: "pl-smi",
          children: "string"
        }), ",\n\t\t", _jsx(_components.span, {
          class: "pl-smi",
          children: "type"
        }), ": ", _jsxs(_components.span, {
          class: "pl-s",
          children: [_jsx(_components.span, {
            class: "pl-pds",
            children: "\""
          }), "songs", _jsx(_components.span, {
            class: "pl-pds",
            children: "\""
          })]
        }), _jsx(_components.span, {
          class: "pl-k",
          children: "|"
        }), _jsxs(_components.span, {
          class: "pl-s",
          children: [_jsx(_components.span, {
            class: "pl-pds",
            children: "\""
          }), "library-songs", _jsx(_components.span, {
            class: "pl-pds",
            children: "\""
          })]
        }), _jsx(_components.span, {
          class: "pl-k",
          children: "|"
        }), _jsxs(_components.span, {
          class: "pl-s",
          children: [_jsx(_components.span, {
            class: "pl-pds",
            children: "\""
          }), "music-videos", _jsx(_components.span, {
            class: "pl-pds",
            children: "\""
          })]
        }), ",\n\t  ]\n\t}\n  }\n}\n"]
      })
    }), "\n", _jsx(_components.p, {
      children: _jsx(_components.strong, {
        children: "What the response would look like:"
      })
    }), "\n", _jsx(_components.pre, {
      children: _jsxs(_components.code, {
        class: "language-ts",
        children: ["[\n  {\n  \tid: ", _jsx(_components.span, {
          class: "pl-smi",
          children: "string"
        }), ";\n  \t", _jsx(_components.span, {
          class: "pl-smi",
          children: "type"
        }), ": ", _jsxs(_components.span, {
          class: "pl-s",
          children: [_jsx(_components.span, {
            class: "pl-pds",
            children: "\""
          }), "library-playlists", _jsx(_components.span, {
            class: "pl-pds",
            children: "\""
          })]
        }), ",\n  \tattributes: ", _jsx(_components.span, {
          class: "pl-smi",
          children: "any"
        }), ",\n  }\n]\n"]
      })
    }), "\n", _jsx(_components.h2, {
      children: "Other"
    }), "\n", _jsx(_components.h3, {
      children: "Search"
    }), "\n", _jsxs(_components.p, {
      children: [_jsx(_components.strong, {
        children: "Endpoint:"
      }), " ", _jsx(_components.code, {
        children: "GET /v1/catalog/ca/search"
      }), "\nThis one's a bit tricky. I haven't quite cracked how it handles parameters, but here's what parameters I use in Transcribe to search for only songs:"]
    }), "\n", _jsx(_components.pre, {
      children: _jsxs(_components.code, {
        class: "language-js",
        children: [_jsx(_components.span, {
          class: "pl-k",
          children: "let"
        }), " params ", _jsx(_components.span, {
          class: "pl-k",
          children: "="
        }), " {\n    ", _jsxs(_components.span, {
          class: "pl-s",
          children: [_jsx(_components.span, {
            class: "pl-pds",
            children: "\""
          }), "art[music-videos:url]", _jsx(_components.span, {
            class: "pl-pds",
            children: "\""
          })]
        }), _jsx(_components.span, {
          class: "pl-k",
          children: ":"
        }), " ", _jsxs(_components.span, {
          class: "pl-s",
          children: [_jsx(_components.span, {
            class: "pl-pds",
            children: "\""
          }), "c", _jsx(_components.span, {
            class: "pl-pds",
            children: "\""
          })]
        }), ",\n    ", _jsxs(_components.span, {
          class: "pl-s",
          children: [_jsx(_components.span, {
            class: "pl-pds",
            children: "\""
          }), "art[url]", _jsx(_components.span, {
            class: "pl-pds",
            children: "\""
          })]
        }), _jsx(_components.span, {
          class: "pl-k",
          children: ":"
        }), " ", _jsxs(_components.span, {
          class: "pl-s",
          children: [_jsx(_components.span, {
            class: "pl-pds",
            children: "\""
          }), "f", _jsx(_components.span, {
            class: "pl-pds",
            children: "\""
          })]
        }), ",\n    extend", _jsx(_components.span, {
          class: "pl-k",
          children: ":"
        }), " ", _jsxs(_components.span, {
          class: "pl-s",
          children: [_jsx(_components.span, {
            class: "pl-pds",
            children: "\""
          }), "artistUrl", _jsx(_components.span, {
            class: "pl-pds",
            children: "\""
          })]
        }), ",\n    ", _jsxs(_components.span, {
          class: "pl-s",
          children: [_jsx(_components.span, {
            class: "pl-pds",
            children: "\""
          }), "fields[albums]", _jsx(_components.span, {
            class: "pl-pds",
            children: "\""
          })]
        }), _jsx(_components.span, {
          class: "pl-k",
          children: ":"
        }), "\n        ", _jsxs(_components.span, {
          class: "pl-s",
          children: [_jsx(_components.span, {
            class: "pl-pds",
            children: "\""
          }), "artistName,artistUrl,artwork,contentRating,editorialArtwork,editorialNotes,name,playParams,releaseDate,url,trackCount", _jsx(_components.span, {
            class: "pl-pds",
            children: "\""
          })]
        }), ",\n    ", _jsxs(_components.span, {
          class: "pl-s",
          children: [_jsx(_components.span, {
            class: "pl-pds",
            children: "\""
          }), "fields[artists]", _jsx(_components.span, {
            class: "pl-pds",
            children: "\""
          })]
        }), _jsx(_components.span, {
          class: "pl-k",
          children: ":"
        }), " ", _jsxs(_components.span, {
          class: "pl-s",
          children: [_jsx(_components.span, {
            class: "pl-pds",
            children: "\""
          }), "url,name,artwork", _jsx(_components.span, {
            class: "pl-pds",
            children: "\""
          })]
        }), ",\n    ", _jsxs(_components.span, {
          class: "pl-s",
          children: [_jsx(_components.span, {
            class: "pl-pds",
            children: "\""
          }), "format[resources]", _jsx(_components.span, {
            class: "pl-pds",
            children: "\""
          })]
        }), _jsx(_components.span, {
          class: "pl-k",
          children: ":"
        }), " ", _jsxs(_components.span, {
          class: "pl-s",
          children: [_jsx(_components.span, {
            class: "pl-pds",
            children: "\""
          }), "map", _jsx(_components.span, {
            class: "pl-pds",
            children: "\""
          })]
        }), ",\n    ", _jsxs(_components.span, {
          class: "pl-s",
          children: [_jsx(_components.span, {
            class: "pl-pds",
            children: "\""
          }), "include[albums]", _jsx(_components.span, {
            class: "pl-pds",
            children: "\""
          })]
        }), _jsx(_components.span, {
          class: "pl-k",
          children: ":"
        }), " ", _jsxs(_components.span, {
          class: "pl-s",
          children: [_jsx(_components.span, {
            class: "pl-pds",
            children: "\""
          }), "artists", _jsx(_components.span, {
            class: "pl-pds",
            children: "\""
          })]
        }), ",\n    ", _jsxs(_components.span, {
          class: "pl-s",
          children: [_jsx(_components.span, {
            class: "pl-pds",
            children: "\""
          }), "include[songs]", _jsx(_components.span, {
            class: "pl-pds",
            children: "\""
          })]
        }), _jsx(_components.span, {
          class: "pl-k",
          children: ":"
        }), " ", _jsxs(_components.span, {
          class: "pl-s",
          children: [_jsx(_components.span, {
            class: "pl-pds",
            children: "\""
          }), "artists", _jsx(_components.span, {
            class: "pl-pds",
            children: "\""
          })]
        }), ",\n    l", _jsx(_components.span, {
          class: "pl-k",
          children: ":"
        }), " ", _jsxs(_components.span, {
          class: "pl-s",
          children: [_jsx(_components.span, {
            class: "pl-pds",
            children: "\""
          }), "en-CA", _jsx(_components.span, {
            class: "pl-pds",
            children: "\""
          })]
        }), ",\n    limit", _jsx(_components.span, {
          class: "pl-k",
          children: ":"
        }), " limit,\n    ", _jsxs(_components.span, {
          class: "pl-s",
          children: [_jsx(_components.span, {
            class: "pl-pds",
            children: "\""
          }), "omit[resource]", _jsx(_components.span, {
            class: "pl-pds",
            children: "\""
          })]
        }), _jsx(_components.span, {
          class: "pl-k",
          children: ":"
        }), " ", _jsxs(_components.span, {
          class: "pl-s",
          children: [_jsx(_components.span, {
            class: "pl-pds",
            children: "\""
          }), "autos", _jsx(_components.span, {
            class: "pl-pds",
            children: "\""
          })]
        }), ",\n    platform", _jsx(_components.span, {
          class: "pl-k",
          children: ":"
        }), " ", _jsxs(_components.span, {
          class: "pl-s",
          children: [_jsx(_components.span, {
            class: "pl-pds",
            children: "\""
          }), "web", _jsx(_components.span, {
            class: "pl-pds",
            children: "\""
          })]
        }), ",\n    ", _jsxs(_components.span, {
          class: "pl-s",
          children: [_jsx(_components.span, {
            class: "pl-pds",
            children: "\""
          }), "relate[albums]", _jsx(_components.span, {
            class: "pl-pds",
            children: "\""
          })]
        }), _jsx(_components.span, {
          class: "pl-k",
          children: ":"
        }), " ", _jsxs(_components.span, {
          class: "pl-s",
          children: [_jsx(_components.span, {
            class: "pl-pds",
            children: "\""
          }), "artists", _jsx(_components.span, {
            class: "pl-pds",
            children: "\""
          })]
        }), ",\n    ", _jsxs(_components.span, {
          class: "pl-s",
          children: [_jsx(_components.span, {
            class: "pl-pds",
            children: "\""
          }), "relate[songs]", _jsx(_components.span, {
            class: "pl-pds",
            children: "\""
          })]
        }), _jsx(_components.span, {
          class: "pl-k",
          children: ":"
        }), " ", _jsxs(_components.span, {
          class: "pl-s",
          children: [_jsx(_components.span, {
            class: "pl-pds",
            children: "\""
          }), "albums", _jsx(_components.span, {
            class: "pl-pds",
            children: "\""
          })]
        }), ",\n    term", _jsx(_components.span, {
          class: "pl-k",
          children: ":"
        }), " query,\n    types", _jsx(_components.span, {
          class: "pl-k",
          children: ":"
        }), " ", _jsxs(_components.span, {
          class: "pl-s",
          children: [_jsx(_components.span, {
            class: "pl-pds",
            children: "\""
          }), "songs", _jsx(_components.span, {
            class: "pl-pds",
            children: "\""
          })]
        }), ",\n    with", _jsx(_components.span, {
          class: "pl-k",
          children: ":"
        }), " ", _jsxs(_components.span, {
          class: "pl-s",
          children: [_jsx(_components.span, {
            class: "pl-pds",
            children: "\""
          }), "lyricHighlights,lyrics,serverBubbles,subtitles", _jsx(_components.span, {
            class: "pl-pds",
            children: "\""
          })]
        }), ",\n};\n"]
      })
    }), "\n", _jsx(_components.p, {
      children: _jsx(_components.strong, {
        children: "Relevant types for the response:"
      })
    }), "\n", _jsx(_components.pre, {
      children: _jsxs(_components.code, {
        class: "language-ts",
        children: [_jsx(_components.span, {
          class: "pl-c",
          children: "/**"
        }), "\n", _jsx(_components.span, {
          class: "pl-c",
          children: " * The top-level structure of the Apple Music API search response."
        }), "\n", _jsx(_components.span, {
          class: "pl-c",
          children: " */"
        }), "\n", _jsx(_components.span, {
          class: "pl-k",
          children: "interface"
        }), " ", _jsx(_components.span, {
          class: "pl-en",
          children: "AMSearchResponse"
        }), " {\n    ", _jsx(_components.span, {
          class: "pl-v",
          children: "results"
        }), _jsx(_components.span, {
          class: "pl-k",
          children: ":"
        }), " ", _jsx(_components.span, {
          class: "pl-en",
          children: "AMSearchResults"
        }), ";\n    ", _jsx(_components.span, {
          class: "pl-v",
          children: "resources"
        }), _jsx(_components.span, {
          class: "pl-k",
          children: "?:"
        }), " ", _jsx(_components.span, {
          class: "pl-en",
          children: "AMResourcesContainer"
        }), "; ", _jsx(_components.span, {
          class: "pl-c",
          children: "// Included full resources (optional based on request/results)"
        }), "\n    ", _jsx(_components.span, {
          class: "pl-v",
          children: "meta"
        }), _jsx(_components.span, {
          class: "pl-k",
          children: "?:"
        }), " ", _jsx(_components.span, {
          class: "pl-en",
          children: "AMSearchMeta"
        }), "; ", _jsx(_components.span, {
          class: "pl-c",
          children: "// Optional meta information"
        }), "\n}\n\n", _jsx(_components.span, {
          class: "pl-c",
          children: "/**"
        }), "\n", _jsx(_components.span, {
          class: "pl-c",
          children: " * Represents the 'results' part of the search response, containing different groups."
        }), "\n", _jsx(_components.span, {
          class: "pl-c",
          children: " * Keys are typically the `groupId` values."
        }), "\n", _jsx(_components.span, {
          class: "pl-c",
          children: " */"
        }), "\n", _jsx(_components.span, {
          class: "pl-k",
          children: "interface"
        }), " ", _jsx(_components.span, {
          class: "pl-en",
          children: "AMSearchResults"
        }), " {\n    ", _jsx(_components.span, {
          class: "pl-v",
          children: "top"
        }), _jsx(_components.span, {
          class: "pl-k",
          children: "?:"
        }), " ", _jsx(_components.span, {
          class: "pl-en",
          children: "AMTopSearchResultGroup"
        }), ";\n    ", _jsx(_components.span, {
          class: "pl-v",
          children: "artist"
        }), _jsx(_components.span, {
          class: "pl-k",
          children: "?:"
        }), " ", _jsx(_components.span, {
          class: "pl-en",
          children: "AMSearchResultGroup"
        }), "<", _jsx(_components.span, {
          class: "pl-en",
          children: "AMResourceReference"
        }), ">;\n    ", _jsx(_components.span, {
          class: "pl-v",
          children: "album"
        }), _jsx(_components.span, {
          class: "pl-k",
          children: "?:"
        }), " ", _jsx(_components.span, {
          class: "pl-en",
          children: "AMSearchResultGroup"
        }), "<", _jsx(_components.span, {
          class: "pl-en",
          children: "AMResourceReference"
        }), ">;\n    ", _jsx(_components.span, {
          class: "pl-v",
          children: "song"
        }), _jsx(_components.span, {
          class: "pl-k",
          children: "?:"
        }), " ", _jsx(_components.span, {
          class: "pl-en",
          children: "AMSearchResultGroup"
        }), "<", _jsx(_components.span, {
          class: "pl-en",
          children: "AMResourceReference"
        }), ">;\n    ", _jsx(_components.span, {
          class: "pl-v",
          children: "playlist"
        }), _jsx(_components.span, {
          class: "pl-k",
          children: "?:"
        }), " ", _jsx(_components.span, {
          class: "pl-en",
          children: "AMSearchResultGroup"
        }), "<", _jsx(_components.span, {
          class: "pl-en",
          children: "AMResourceReference"
        }), ">;\n    ", _jsxs(_components.span, {
          class: "pl-s",
          children: [_jsx(_components.span, {
            class: "pl-pds",
            children: "'"
          }), "radio_episode", _jsx(_components.span, {
            class: "pl-pds",
            children: "'"
          })]
        }), _jsx(_components.span, {
          class: "pl-k",
          children: "?:"
        }), " ", _jsx(_components.span, {
          class: "pl-en",
          children: "AMSearchResultGroup"
        }), "<", _jsx(_components.span, {
          class: "pl-en",
          children: "AMResourceReference"
        }), ">; ", _jsx(_components.span, {
          class: "pl-c",
          children: "// Note: uses station type reference"
        }), "\n    ", _jsx(_components.span, {
          class: "pl-v",
          children: "station"
        }), _jsx(_components.span, {
          class: "pl-k",
          children: "?:"
        }), " ", _jsx(_components.span, {
          class: "pl-en",
          children: "AMSearchResultGroup"
        }), "<", _jsx(_components.span, {
          class: "pl-en",
          children: "AMResourceReference"
        }), ">;\n    ", _jsxs(_components.span, {
          class: "pl-s",
          children: [_jsx(_components.span, {
            class: "pl-pds",
            children: "'"
          }), "music_video", _jsx(_components.span, {
            class: "pl-pds",
            children: "'"
          })]
        }), _jsx(_components.span, {
          class: "pl-k",
          children: "?:"
        }), " ", _jsx(_components.span, {
          class: "pl-en",
          children: "AMSearchResultGroup"
        }), "<", _jsx(_components.span, {
          class: "pl-en",
          children: "AMResourceReference"
        }), ">;\n    ", _jsxs(_components.span, {
          class: "pl-s",
          children: [_jsx(_components.span, {
            class: "pl-pds",
            children: "'"
          }), "video_extra", _jsx(_components.span, {
            class: "pl-pds",
            children: "'"
          })]
        }), _jsx(_components.span, {
          class: "pl-k",
          children: "?:"
        }), " ", _jsx(_components.span, {
          class: "pl-en",
          children: "AMSearchResultGroup"
        }), "<", _jsx(_components.span, {
          class: "pl-en",
          children: "AMResourceReference"
        }), ">; ", _jsx(_components.span, {
          class: "pl-c",
          children: "// Note: uses uploaded-video type reference"
        }), "\n    [", _jsx(_components.span, {
          class: "pl-v",
          children: "key"
        }), _jsx(_components.span, {
          class: "pl-k",
          children: ":"
        }), " ", _jsx(_components.span, {
          class: "pl-c1",
          children: "string"
        }), "]", _jsx(_components.span, {
          class: "pl-k",
          children: ":"
        }), " ", _jsx(_components.span, {
          class: "pl-en",
          children: "AMTopSearchResultGroup"
        }), " ", _jsx(_components.span, {
          class: "pl-k",
          children: "|"
        }), " ", _jsx(_components.span, {
          class: "pl-en",
          children: "AMSearchResultGroup"
        }), "<", _jsx(_components.span, {
          class: "pl-c1",
          children: "any"
        }), "> ", _jsx(_components.span, {
          class: "pl-k",
          children: "|"
        }), " ", _jsx(_components.span, {
          class: "pl-c1",
          children: "undefined"
        }), "; ", _jsx(_components.span, {
          class: "pl-c",
          children: "// Index signature for flexibility"
        }), "\n}\n\n", _jsx(_components.span, {
          class: "pl-c",
          children: "/**"
        }), "\n", _jsx(_components.span, {
          class: "pl-c",
          children: " * Represents the 'top' results group which contains mixed types."
        }), "\n", _jsx(_components.span, {
          class: "pl-c",
          children: " */"
        }), "\n", _jsx(_components.span, {
          class: "pl-k",
          children: "interface"
        }), " ", _jsx(_components.span, {
          class: "pl-en",
          children: "AMTopSearchResultGroup"
        }), " {\n    ", _jsx(_components.span, {
          class: "pl-c",
          children: "// No href/next at this level"
        }), "\n    ", _jsx(_components.span, {
          class: "pl-v",
          children: "data"
        }), _jsx(_components.span, {
          class: "pl-k",
          children: ":"
        }), " ", _jsx(_components.span, {
          class: "pl-en",
          children: "AMResourceReference"
        }), "[];\n    ", _jsx(_components.span, {
          class: "pl-v",
          children: "name"
        }), _jsx(_components.span, {
          class: "pl-k",
          children: ":"
        }), " ", _jsx(_components.span, {
          class: "pl-c1",
          children: "string"
        }), "; ", _jsx(_components.span, {
          class: "pl-c",
          children: "// e.g., \"Top Results\""
        }), "\n    ", _jsx(_components.span, {
          class: "pl-v",
          children: "groupId"
        }), _jsx(_components.span, {
          class: "pl-k",
          children: ":"
        }), " ", _jsxs(_components.span, {
          class: "pl-s",
          children: [_jsx(_components.span, {
            class: "pl-pds",
            children: "'"
          }), "top", _jsx(_components.span, {
            class: "pl-pds",
            children: "'"
          })]
        }), ";\n}\n\n", _jsx(_components.span, {
          class: "pl-c",
          children: "/**"
        }), "\n", _jsx(_components.span, {
          class: "pl-c",
          children: " * Represents a group of search results for a specific type (e.g., artists, albums)."
        }), "\n", _jsx(_components.span, {
          class: "pl-c",
          children: " */"
        }), "\n", _jsx(_components.span, {
          class: "pl-k",
          children: "interface"
        }), " ", _jsx(_components.span, {
          class: "pl-en",
          children: "AMSearchResultGroup"
        }), "<", _jsx(_components.span, {
          class: "pl-en",
          children: "T"
        }), " ", _jsx(_components.span, {
          class: "pl-k",
          children: "extends"
        }), " ", _jsx(_components.span, {
          class: "pl-en",
          children: "AMResourceReference"
        }), "> {\n    ", _jsx(_components.span, {
          class: "pl-v",
          children: "href"
        }), _jsx(_components.span, {
          class: "pl-k",
          children: "?:"
        }), " ", _jsx(_components.span, {
          class: "pl-c1",
          children: "string"
        }), "; ", _jsx(_components.span, {
          class: "pl-c",
          children: "// Link to refine search for this type"
        }), "\n    ", _jsx(_components.span, {
          class: "pl-v",
          children: "next"
        }), _jsx(_components.span, {
          class: "pl-k",
          children: "?:"
        }), " ", _jsx(_components.span, {
          class: "pl-c1",
          children: "string"
        }), "; ", _jsx(_components.span, {
          class: "pl-c",
          children: "// Link to next page of results for this type"
        }), "\n    ", _jsx(_components.span, {
          class: "pl-v",
          children: "data"
        }), _jsx(_components.span, {
          class: "pl-k",
          children: ":"
        }), " ", _jsx(_components.span, {
          class: "pl-en",
          children: "T"
        }), "[]; ", _jsx(_components.span, {
          class: "pl-c",
          children: "// Array of references for this type"
        }), "\n    ", _jsx(_components.span, {
          class: "pl-v",
          children: "name"
        }), _jsx(_components.span, {
          class: "pl-k",
          children: ":"
        }), " ", _jsx(_components.span, {
          class: "pl-c1",
          children: "string"
        }), "; ", _jsx(_components.span, {
          class: "pl-c",
          children: "// Display name (e.g., \"Artists\")"
        }), "\n    ", _jsx(_components.span, {
          class: "pl-v",
          children: "groupId"
        }), _jsx(_components.span, {
          class: "pl-k",
          children: ":"
        }), " ", _jsx(_components.span, {
          class: "pl-c1",
          children: "string"
        }), "; ", _jsx(_components.span, {
          class: "pl-c",
          children: "// Internal group ID (e.g., \"artist\")"
        }), "\n}\n\n", _jsx(_components.span, {
          class: "pl-c",
          children: "/**"
        }), "\n", _jsx(_components.span, {
          class: "pl-c",
          children: " * Represents the 'meta' part of the search response."
        }), "\n", _jsx(_components.span, {
          class: "pl-c",
          children: " */"
        }), "\n", _jsx(_components.span, {
          class: "pl-k",
          children: "interface"
        }), " ", _jsx(_components.span, {
          class: "pl-en",
          children: "AMSearchMeta"
        }), " {\n    ", _jsx(_components.span, {
          class: "pl-v",
          children: "results"
        }), _jsx(_components.span, {
          class: "pl-k",
          children: ":"
        }), " {\n        ", _jsx(_components.span, {
          class: "pl-v",
          children: "order"
        }), _jsx(_components.span, {
          class: "pl-k",
          children: ":"
        }), " ", _jsx(_components.span, {
          class: "pl-c1",
          children: "string"
        }), "[]; ", _jsx(_components.span, {
          class: "pl-c",
          children: "// Array of groupIds in display order"
        }), "\n        ", _jsx(_components.span, {
          class: "pl-v",
          children: "rawOrder"
        }), _jsx(_components.span, {
          class: "pl-k",
          children: ":"
        }), " ", _jsx(_components.span, {
          class: "pl-c1",
          children: "string"
        }), "[]; ", _jsx(_components.span, {
          class: "pl-c",
          children: "// Array of groupIds potentially including types with no results"
        }), "\n    };\n    ", _jsx(_components.span, {
          class: "pl-v",
          children: "metrics"
        }), _jsx(_components.span, {
          class: "pl-k",
          children: "?:"
        }), " { ", _jsx(_components.span, {
          class: "pl-c",
          children: "// Optional metrics data"
        }), "\n        ", _jsx(_components.span, {
          class: "pl-v",
          children: "dataSetId"
        }), _jsx(_components.span, {
          class: "pl-k",
          children: ":"
        }), " ", _jsx(_components.span, {
          class: "pl-c1",
          children: "string"
        }), ";\n    };\n}\n\n"]
      })
    })]
  });
}
export function MDXContent(props = {}) {
  const {wrapper: MDXLayout} = props.components || ({});
  return MDXLayout ? _jsx(MDXLayout, {
    ...props,
    children: _jsx(_createMdxContent, {
      ...props
    })
  }) : _createMdxContent(props);
}


		export default function Page() {
			const {wrapper: MDXLayout} = this.components || ({});
			return (
				MDXLayout 
					? _jsx(MDXLayout, { children: [_createMdxContent(this)], ...this })
					: _createMdxContent(this)
			)
		}
	